import { PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { Button, Flex, IconButton } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import {
  BaseballIcon,
  MachineIcon,
} from 'components/common/custom-icon/shorthands';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonMenu } from 'components/common/menu';
import { CommonTableHoC } from 'components/common/table';
import useBurnInTestStore from 'components/sections/admin-portal/machines/dialogs/settings/machine-burn-in/use-burn-in-test-store';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ITableSelectable } from 'interfaces/tables/selection';
import { ITableSortable } from 'interfaces/tables/sorting';
import { BurnInTestType } from 'lib_ts/enums/machine.enums';
import { ReferenceListType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IBurnInTest } from 'lib_ts/interfaces/i-burn-in-test';
import { IPitchList } from 'lib_ts/interfaces/pitches';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AdminBurnInTestsService } from 'services/admin/machine-burn-in-tests.service';
import { PitchListsService } from 'services/pitch-lists.service';
import { useAuthStore } from 'stores/use-auth-store';

const COMPONENT_NAME = 'MachineBurnInTable';

export const MachineBurnInTable = () => {
  const current = useAuthStore((s) => s.current);
  const machine = useMachinesPortalStore((s) => s.machine);

  const tests = useBurnInTestStore((s) => s.tests);

  const [refFiringList, setRefFiringList] = useState<IPitchList>();

  // find the first burn-in list, required to create new tests
  useEffect(() => {
    PitchListsService.getInstance()
      .getLiveReferences()
      .then((results) => {
        setRefFiringList(
          results.find((m) => m.reference_type === ReferenceListType.BurnInTest)
        );
      });
  }, []);

  useEffect(() => {
    // reset everything when the machine changes
    useBurnInTestStore.getState().reset();

    if (!machine?.machineID) {
      // nothing new to load
      return;
    }

    AdminBurnInTestsService.getInstance()
      .search({ machineID: machine.machineID })
      .then((results) => useBurnInTestStore.setState({ tests: results }));
  }, [machine?.machineID]);

  const columns = useMemo<ITypedTableColumn<IBurnInTest>[]>(
    () => [
      {
        key: 'type',
        label: 'Type',
        classNameFn: () => 'capitalize',
      },
      {
        key: 'start',
        dataType: 'datetime',
        label: 'Start',
      },
      {
        key: 'end',
        dataType: 'datetime',
        label: 'End',
      },
      {
        key: 'performedBy',
        label: 'Started By',
      },
      {
        key: 'errors',
        label: 'Errors',
        formatFn: (m) => (m.end ? m.errors.length : null),
      },
      {
        key: '_delete',
        label: 'Delete',
        formatFn: (m) => (
          <IconButton
            size="1"
            color={RADIX.COLOR.DANGER}
            variant={RADIX.BUTTON.VARIANT.SECONDARY}
            onClick={(e) => {
              // todo: maybe we block deleting after a test has ended?

              // prevent activating the test when trying to delete it
              e.stopPropagation();

              AdminBurnInTestsService.getInstance()
                .deleteOne(m._id)
                .then((success) => {
                  if (!success) {
                    return;
                  }

                  // omit the deleted test from what's been loaded
                  useBurnInTestStore.setState((s) => ({
                    tests: s.tests.filter((p) => p._id !== m._id),
                  }));
                });
            }}
          >
            <TrashIcon />
          </IconButton>
        ),
      },
    ],
    []
  );

  const clickable: ITableSelectable = {
    afterChangeSelected: (model: IBurnInTest) =>
      useBurnInTestStore.setState({ activeTestID: model._id }),
    enableSelect: true,
  };

  const sort: ITableSortable = {
    defaultSort: {
      key: 'start',
      dir: -1,
    },
    enableSort: true,
  };

  const handleCreate = useCallback(
    async (type: BurnInTestType) => {
      if (!machine?.machineID) {
        return;
      }

      if (!refFiringList && type === 'firing') {
        NotifyHelper.error({
          message_md:
            'Please define a burn-in test reference list and make it live first.',
        });
        return;
      }

      const newTest = await AdminBurnInTestsService.getInstance().postOne({
        machineID: machine.machineID,
        type: type,
        performedBy: current.email,
        start: new Date().toISOString(),
        end: null,
        firingTest:
          type === BurnInTestType.Firing && refFiringList
            ? {
                listID: refFiringList._id,
                processed: {},
                shots: {},
              }
            : null,
        rangeTest:
          type === BurnInTestType.Range
            ? {
                processed: {},
              }
            : null,
        errors: [],
      });

      if (!newTest) {
        return;
      }

      useBurnInTestStore.setState((s) => ({
        tests: [...s.tests, newTest],
        activeTestID: newTest._id,
      }));
    },
    [machine?.machineID, refFiringList]
  );

  return (
    <ErrorBoundary identifier={COMPONENT_NAME}>
      <CommonTableHoC
        dataTestID="BurnInTests"
        identifier="BurnInTestsTable"
        displayColumns={columns}
        displayData={tests}
        scrollHeight="400px"
        skipResetSelected
        toolbarContent={
          <Flex justify="end">
            <CommonMenu
              title="Create Burn-In Test"
              trigger={
                <Button size={RADIX.BUTTON.SIZE.TABLE}>
                  <PlusIcon /> Create
                </Button>
              }
              actions={[
                {
                  label: 'Firing Test',
                  prefixIcon: <BaseballIcon />,
                  onClick: () => handleCreate(BurnInTestType.Firing),
                },
                {
                  label: 'Range Test',
                  prefixIcon: <MachineIcon />,
                  onClick: () => handleCreate(BurnInTestType.Range),
                },
              ]}
            />
          </Flex>
        }
        {...clickable}
        {...sort}
      />
    </ErrorBoundary>
  );
};
